'use client'

import { FC, useRef } from 'react'
import { AriaTabProps, useTab } from 'react-aria'
import { Node, TabListState } from 'react-stately'

import { cn } from '../../utils/cn'

interface TabProps extends AriaTabProps {
  item: Node<object>
  state: TabListState<object>
  variant?: 'pills' | 'underline' | 'bordered'
  distribution?: 'auto' | 'equal'
}

export const TabButton: FC<TabProps> = ({
  item,
  state,
  variant = 'pills',
  distribution = 'auto'
}) => {
  const { key, rendered } = item
  const ref = useRef(null)
  const { tabProps } = useTab({ key }, state, ref)
  const isSelected = state.selectedKey === key

  // Variant-specific styles
  const variantStyles = {
    pills: cn(
      'flex flex-1 grow cursor-pointer items-center justify-center rounded-lg px-3 py-1.5',
      'outline-none ring-blue-300 transition-colors duration-300',
      'hover:bg-blue-200 focus:outline-none focus:ring-2',
      'aria-selected:bg-gray-200'
    ),
    underline: cn(
      'relative flex flex-col items-center gap-3 px-0 pb-0 pt-3 transition-colors',
      distribution === 'equal' && 'min-h-px min-w-px grow basis-0',
      distribution === 'auto' && 'px-1',
      isSelected
        ? 'text-[16px] font-bold text-blue-800'
        : 'text-[16px] font-medium text-slate-700'
    ),
    bordered: cn(
      'flex flex-1 grow cursor-pointer items-center justify-center rounded-md px-3 py-1.5',
      'outline-none transition-colors duration-300',
      'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300',
      'aria-selected:bg-blue-100 aria-selected:text-blue-800'
    )
  }

  return (
    <button {...tabProps} ref={ref} className={variantStyles[variant]}>
      <span className={variant === 'underline' ? 'leading-[24px]' : ''}>
        {rendered}
      </span>
      {/* Underline indicator for underline variant */}
      {variant === 'underline' && (
        <div
          className={cn(
            'h-1 transition-all duration-200',
            isSelected
              ? 'w-full bg-blue-800'
              : 'w-[59px] bg-slate-600 opacity-0'
          )}
        />
      )}
    </button>
  )
}
